/**
 * SettingsCustomerAccounts — Iter 191u · Selling › Customer Accounts
 *
 * Vendor-side toggles that control the storefront's customer-account UX.
 *
 * Persistence:
 *   GET  /api/builder/customer-accounts
 *   PUT  /api/builder/customer-accounts
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Save, Loader2, Mail, UserCheck } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { GlassCard, DashboardBackdrop, GradientIconTile } from '@/components/dashboard/dashboard-ui';
import BackToSettings from './_BackToSettings';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const DEFAULTS = {
  enabled: true,
  guest_checkout: true,
  welcome_message: 'Sign in to view your orders and manage your saved details.',
  auto_save_addresses: true,
  auto_save_payment_methods: false,
};

export default function SettingsCustomerAccounts() {
  const [state, setState] = useState(DEFAULTS);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/builder/customer-accounts`, { headers: auth() });
        setState({ ...DEFAULTS, ...(data || {}) });
      } catch (_) { /* silent */ }
      try {
        const { data } = await axios.get(`${API}/builder/customer-accounts/customers`, { headers: auth() });
        setCustomers(data?.customers || []);
      } catch (_) { /* silent */ }
      setLoading(false);
    })();
  }, []);

  const set = (k, v) => setState((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/builder/customer-accounts`, state, { headers: auth() });
      toast.success('Customer account settings saved');
    } catch { toast.error('Could not save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="relative" data-testid="settings-customer-accounts-page">
      <DashboardBackdrop />
      <div className="max-w-3xl mx-auto py-10 px-4 space-y-6">
        <BackToSettings />

        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] tracking-[0.18em] font-bold uppercase text-[#A78BFA]">Selling</p>
            <h1 className="text-3xl font-bold text-white font-syne mt-1">Customer Accounts</h1>
            <p className="text-sm text-white/65 mt-2 max-w-xl">
              Let buyers create passwordless accounts on your storefront so they can view order history, track
              shipments, and reuse saved addresses at checkout. Sign-in is via magic link — no password required.
            </p>
          </div>
          <button
            onClick={save}
            disabled={saving || loading}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] disabled:opacity-50 transition-all"
            data-testid="customer-accounts-save-btn"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>

        {loading ? (
          <div className="text-white/50 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <GlassCard className="p-6" data-testid="customer-accounts-toggle-card">
              <div className="flex items-start gap-4 mb-5">
                <GradientIconTile icon={Users} ramp="violet" />
                <div className="flex-1">
                  <h2 className="text-white font-bold font-syne">Customer accounts</h2>
                  <p className="text-xs text-white/55 mt-0.5">
                    Turn accounts on to let buyers sign in and access their history. Off = checkout only, no account section.
                  </p>
                </div>
              </div>

              <Row
                label="Enable customer accounts"
                sub="Adds a Sign-In link to your storefront header + /account page."
                checked={state.enabled}
                onChange={(v) => set('enabled', v)}
                testid="customer-accounts-enabled-toggle"
              />
              <Row
                label="Allow guest checkout"
                sub="Buyers can still purchase without signing in."
                checked={state.guest_checkout}
                onChange={(v) => set('guest_checkout', v)}
                testid="customer-accounts-guest-toggle"
                disabled={!state.enabled}
              />
              <Row
                label="Auto-save shipping addresses"
                sub="When a buyer signs in and checks out, save the shipping address to their account."
                checked={state.auto_save_addresses}
                onChange={(v) => set('auto_save_addresses', v)}
                testid="customer-accounts-autosave-addr-toggle"
                disabled={!state.enabled}
              />
              <Row
                label="Auto-save payment methods"
                sub="Store the card via Stripe so returning buyers can pay in one click. (Requires Stripe Connect.)"
                checked={state.auto_save_payment_methods}
                onChange={(v) => set('auto_save_payment_methods', v)}
                testid="customer-accounts-autosave-pm-toggle"
                disabled={!state.enabled}
              />
            </GlassCard>

            <GlassCard className="p-6" data-testid="customer-accounts-welcome-card">
              <div className="flex items-start gap-4 mb-4">
                <GradientIconTile icon={Mail} ramp="cyan" />
                <div className="flex-1">
                  <h2 className="text-white font-bold font-syne">Welcome message</h2>
                  <p className="text-xs text-white/55 mt-0.5">
                    Shown on the storefront sign-in page above the email input. Great place to explain what buyers get out of an account.
                  </p>
                </div>
              </div>
              <Label className="text-[#94A3B8] text-xs">Message</Label>
              <Textarea
                rows={3}
                value={state.welcome_message}
                onChange={(e) => set('welcome_message', e.target.value)}
                placeholder="Sign in to view your orders and manage your saved details."
                className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl"
                data-testid="customer-accounts-welcome-input"
                maxLength={400}
                disabled={!state.enabled}
              />
              <p className="text-[10.5px] text-white/40 mt-1 text-right">{(state.welcome_message || '').length}/400</p>
            </GlassCard>

            <GlassCard className="p-6" data-testid="customer-accounts-list-card">
              <div className="flex items-start gap-4 mb-4">
                <GradientIconTile icon={UserCheck} ramp="fuchsia" />
                <div className="flex-1">
                  <h2 className="text-white font-bold font-syne">Your customers</h2>
                  <p className="text-xs text-white/55 mt-0.5">
                    Buyers who have created an account on your storefront ({customers.length} total).
                  </p>
                </div>
              </div>

              {customers.length === 0 ? (
                <div className="text-[12px] text-white/40 italic py-6 text-center border border-dashed border-white/10 rounded-xl">
                  No customers yet. Once someone signs in on your storefront, they&rsquo;ll show up here.
                </div>
              ) : (
                <div className="divide-y divide-white/[0.05]">
                  {customers.slice(0, 25).map((c) => (
                    <div key={c.buyer_id} className="py-2.5 grid grid-cols-[1fr_auto] gap-3 items-center text-xs" data-testid={`customer-row-${c.buyer_id}`}>
                      <div className="min-w-0">
                        <div className="text-white font-semibold truncate">{c.name || c.email}</div>
                        <div className="text-white/50 truncate">{c.email}{c.phone ? ` · ${c.phone}` : ''}</div>
                      </div>
                      <div className="text-white/40 text-[10.5px]">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                        {c.marketing_consent && <div className="mt-0.5 inline-block px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 uppercase tracking-wider text-[9.5px] font-bold">Opt-in</div>}
                      </div>
                    </div>
                  ))}
                  {customers.length > 25 && (
                    <div className="text-[10.5px] text-white/40 pt-3 text-center">Showing 25 of {customers.length}</div>
                  )}
                </div>
              )}
            </GlassCard>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, sub, checked, onChange, testid, disabled }) {
  return (
    <div className={`flex items-center justify-between gap-4 py-3 border-b border-white/[0.05] last:border-0 ${disabled ? 'opacity-45' : ''}`}>
      <div className="min-w-0">
        <div className="text-white text-sm font-medium">{label}</div>
        {sub && <div className="text-[11px] text-white/50 mt-0.5">{sub}</div>}
      </div>
      <Switch checked={!!checked} onCheckedChange={onChange} disabled={disabled} data-testid={testid} />
    </div>
  );
}
